import {
  memo,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { startCase } from 'lodash';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import Loader from 'src/common/Loader';
import useDidUpdate from 'src/hooks/useDidUpdate';
import { TABLE_ROWS_PER_PAGE } from 'src/utils/constants';
import { getAlignByColumnId } from 'src/utils/tableHeaders';

const COLUMN_LABELS = {
  registration_num: 'Vehicle No',
  vehicle_number: 'Vehicle No',
  group_name: 'Vehicle Group',
  site_name: 'Site Name',
  quantity: 'Fuel Quantity',
  fuel_quantity: 'Fuel Quantity',
  total_quantity: 'Fuel Quantity',
  opening_reading: 'Start Odometer',
  start_odometer: 'Start Odometer',
  start_reading: 'Start Odometer',
  closing_reading: 'End Odometer',
  end_odometer: 'End Odometer',
  end_reading: 'End Odometer',
  mileage: 'km/L',
  km_per_litre: 'km/L',
  km_l: 'km/L',
  l_hr: 'L/HR',
  lph: 'L/HR'
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'NA';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return value;
};

const getHeadersFromData = (data = []) => {
  const firstRow = data[0];
  if (!firstRow) return [];
  return Object.keys(firstRow)
    .filter((key) => typeof firstRow[key] !== 'object' || firstRow[key] === null)
    .map((key) => ({
      id: key,
      name: COLUMN_LABELS[key] || startCase(key),
      align: 'center'
    }));
};

const CustomisedMileageReportTable = ({ onLimitChange, reportName, ...rest }) => {
  const {
    customisedMileageLoading,
    customisedMileageList,
    customisedMileageTotalCount
  } = useSelector((state) => state.reports);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [tableHeaders, setTableHeaders] = useState([]);
  const tableAlignById = useMemo(() => getAlignByColumnId(tableHeaders), [tableHeaders]);

  useEffect(() => {
    const formattedHeaders = getHeadersFromData(customisedMileageList);
    setTableHeaders(formattedHeaders);
    if (formattedHeaders.length > 0) {
      setOrderBy(formattedHeaders[0].id);
    }
  }, [customisedMileageList]);

  useDidUpdate(() => {
    setPage(0);
  }, [reportName]);

  const handleLimitChange = (event) => {
    const newLimit = Number(event.target.value);
    setPage(0);
    setLimit(newLimit);
    onLimitChange({ pageNo: 1, limit: newLimit });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    onLimitChange({ pageNo: newPage + 1, limit });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Card {...rest}>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="h5">Report Table - Customised Mileage Report</Typography>
      </Box>
      <Divider />
      {customisedMileageLoading ? (
        <Loader />
      ) : (
        <PerfectScrollbar>
          <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 }, minHeight: 200 }}>
            {tableHeaders.length > 0 && (
              <Table>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  headCells={tableHeaders}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {customisedMileageList
                    .slice()
                    .sort(getComparator(order, orderBy))
                    .map((report, index) => (
                      <TableRow key={`customised-mileage-report-${index.toString()}`}>
                        {tableHeaders.map((header) => (
                          <TableCell align={tableAlignById[header.id]} key={header.id}>
                            {formatCellValue(report[header.id])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </PerfectScrollbar>
      )}
      <TablePagination
        component="div"
        count={customisedMileageTotalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
      />
    </Card>
  );
};

CustomisedMileageReportTable.propTypes = {
  onLimitChange: PropTypes.func,
  reportName: PropTypes.string
};

export default memo(CustomisedMileageReportTable);
